// /admin — the dashboard's overview: system health, the review queue, the live view (remove a song
// while it plays), thresholds and limits, the reference melody list, and the report and audit logs
// (periods: /admin/planner; sounds: /admin/lab). The song runs without any of this (reviews off).
import { useSession } from "../session";
import { useCallback, useEffect, useState, type ReactNode } from "react";
import { ApiError, api } from "../api";
import { Link, useTitle } from "../lib/router";
import { AdminShell } from "./admin/AdminShell";
import { RemoveSongButton } from "../ui/RemoveSong";

type Tab = "health" | "review" | "live" | "config" | "references" | "reports" | "audit";
const TABS: [Tab, string][] = [
  ["health", "Health"],
  ["review", "Review"],
  ["live", "Live"],
  ["config", "Thresholds & limits"],
  ["references", "Reference melodies"],
  ["reports", "Reports"],
  ["audit", "Audit log"],
];

type Settings = Record<string, Record<string, number | boolean | string[]>>;

function useLoad<T>(url: string): [T | null, string, () => void] {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState("");
  const load = useCallback(() => {
    api<T>("GET", url)
      .then((d) => {
        setData(d);
        setError("");
      })
      .catch((e) => setError(e instanceof ApiError ? e.message : String(e)));
  }, [url]);
  useEffect(load, [load]);
  return [data, error, load];
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="panel stack" aria-label={title}>
      <h2>{title}</h2>
      {children}
    </section>
  );
}

interface HealthData {
  ok: boolean;
  now: number;
  uptimeS: number;
  db: { kind: string; pingMs: number };
  jobs: Record<string, number>;
  checks: { count: number; avgMs: number; last: { ms: number; at: string } | null };
  lineups: { today: boolean; tomorrow: boolean };
  live: { listeners: number; connections: number };
  memory: { rssMb: number; heapMb: number };
  mailer: string;
}

function Stat({ label, value, warn = false }: { label: string; value: ReactNode; warn?: boolean }) {
  return (
    <div className={`stat${warn ? " warn" : ""}`}>
      <span className="stat-label">{label}</span>
      <span className="stat-value">{value}</span>
    </div>
  );
}

function Health() {
  const [h, error, reload] = useLoad<HealthData>("/api/admin/health");
  useEffect(() => {
    const id = setInterval(reload, 5000);
    return () => clearInterval(id);
  }, [reload]);
  return (
    <Section title="System health">
      {error && <p role="alert">{error}</p>}
      {h && (
        <div className="stats" data-testid="health">
          <Stat label="Status" value={h.ok ? "OK" : "Problem"} warn={!h.ok} />
          <Stat label="Uptime" value={`${Math.floor(h.uptimeS / 3600)} h ${Math.floor((h.uptimeS % 3600) / 60)} min`} />
          <Stat label="Database" value={`${h.db.kind} · ${h.db.pingMs} ms`} warn={h.db.pingMs > 200} />
          <Stat label="Listeners" value={`${h.live.listeners} (${h.live.connections} connected)`} />
          <Stat
            label="Lineups"
            value={`today ${h.lineups.today ? "✓" : "✗"} · tomorrow ${h.lineups.tomorrow ? "✓" : "—"}`}
            warn={!h.lineups.today}
          />
          <Stat label="Checks" value={`${h.checks.count} · avg ${h.checks.avgMs} ms`} warn={h.checks.avgMs > 5000} />
          <Stat
            label="Jobs"
            value={
              Object.entries(h.jobs)
                .map(([k, v]) => `${k} ${v}`)
                .join(" · ") || "none"
            }
            warn={(h.jobs.failed ?? 0) > 0}
          />
          <Stat label="Memory" value={`${h.memory.rssMb} MB (heap ${h.memory.heapMb} MB)`} />
          <Stat label="Mail" value={h.mailer} />
          <Stat label="Server time" value={new Date(h.now).toLocaleString()} />
        </div>
      )}
    </Section>
  );
}

/** What a settings section does (shown above its fields). */
const SECTION_HELP: Record<string, string> = {
  repeats:
    "Encores: likes given while a song plays (one per listener, adding up over its plays) earn it another play right after. " +
    "Likes count until bar 12 of 16, then the encore is decided. likesFor2ndPlay … likesFor5thPlay are the totals needed; " +
    "maxPlays caps the plays in a row (1–5, 1 = off). The repeat takes the next slot; songs after it move one slot later up to the next Wizard filler.",
};

function Config() {
  const [data, error, reload] = useLoad<{ settings: Settings; defaults: Settings }>("/api/admin/config");
  const [msg, setMsg] = useState("");
  if (error) return <p role="alert">{error}</p>;
  if (!data) return <p className="muted">Loading…</p>;
  async function save(section: string, key: string, value: number | boolean | string[]) {
    try {
      await api("PUT", `/api/admin/config/${section}`, { [key]: value });
      setMsg(`Saved ${section}.${key}.`);
      reload();
    } catch (e) {
      setMsg(e instanceof ApiError ? e.message : String(e));
    }
  }
  return (
    <>
      {msg && <p role="status">{msg}</p>}
      {Object.entries(data.settings).map(([section, values]) => (
        <Section key={section} title={section}>
          {SECTION_HELP[section] && <p className="muted small">{SECTION_HELP[section]}</p>}
          <div className="admin-grid">
            {Object.entries(values).map(([k, v]) => (
              <label key={k} className="stack-label">
                <span className="small">
                  {k} <span className="muted">(default {JSON.stringify(data.defaults[section][k])})</span>
                </span>
                {typeof v === "boolean" ? (
                  <input type="checkbox" checked={v} onChange={(e) => void save(section, k, e.target.checked)} />
                ) : Array.isArray(v) ? (
                  <textarea
                    defaultValue={v.join("\n")}
                    rows={3}
                    onBlur={(e) =>
                      void save(
                        section,
                        k,
                        e.target.value
                          .split("\n")
                          .map((x) => x.trim())
                          .filter(Boolean),
                      )
                    }
                  />
                ) : (
                  <input
                    type="text"
                    inputMode="decimal"
                    defaultValue={String(v)}
                    onBlur={(e) =>
                      Number.isFinite(Number(e.target.value)) &&
                      Number(e.target.value) !== v &&
                      void save(section, k, Number(e.target.value))
                    }
                  />
                )}
              </label>
            ))}
          </div>
        </Section>
      ))}
    </>
  );
}

function References() {
  const [data, error, reload] = useLoad<{ melodies: { id: number; title: string; source: string; notes: number }[] }>(
    "/api/admin/reference-melodies",
  );
  const [json, setJson] = useState("");
  const [msg, setMsg] = useState("");
  return (
    <>
      <Section title="Import reference melodies (JSON)">
        <p className="muted small">
          An array of{" "}
          <code>{'{ "title": "…", "source": "…", "notes": [{ "note": 60, "tick": 0, "length": 240 }, …] }'}</code>{" "}
          (ticks at 960 per beat). New loops whose melodic lanes are too similar are rejected.
        </p>
        <textarea rows={8} value={json} onChange={(e) => setJson(e.target.value)} aria-label="Reference melody JSON" />
        <div>
          <button
            type="button"
            className="btn btn-primary"
            onClick={async () => {
              try {
                const r = await api<{ imported: number }>("POST", "/api/admin/reference-melodies", JSON.parse(json));
                setMsg(`Imported ${r.imported}.`);
                setJson("");
                reload();
              } catch (e) {
                setMsg(
                  e instanceof ApiError ? e.message : e instanceof SyntaxError ? "That isn't valid JSON." : String(e),
                );
              }
            }}
          >
            Import
          </button>
        </div>
        {msg && <p role="status">{msg}</p>}
      </Section>
      <Section title={`Reference list (${data?.melodies.length ?? 0})`}>
        {error && <p role="alert">{error}</p>}
        <table>
          <thead>
            <tr>
              <th scope="col">Title</th>
              <th scope="col">Source</th>
              <th scope="col">Notes</th>
              <th scope="col">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {data?.melodies.map((m) => (
              <tr key={m.id}>
                <td>{m.title}</td>
                <td>{m.source}</td>
                <td>{m.notes}</td>
                <td>
                  <button
                    type="button"
                    className="btn btn-small"
                    onClick={() => void api("DELETE", `/api/admin/reference-melodies/${m.id}`).then(reload)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Section>
    </>
  );
}

interface ReportedSong {
  loopId: string;
  title: string | null;
  author: string | null;
  bpm: number;
  day: number | null;
  playsAt: number | null;
  hidden: boolean;
  removed: boolean;
  removedReason: string | null;
  open: number;
  outcome: "kept" | "removed" | null;
  lastAt: number;
  reports: { reason: string; note: string | null; reporter: string | null; createdAt: number; handled: boolean }[];
}

const REASON_LABEL: Record<string, string> = {
  copied_melody: "Copied melody",
  offensive_text: "Offensive title or name",
  noise_abuse: "Noise / abuse",
  other: "Other",
};

function ReportedSongItem({ s, onDone }: { s: ReportedSong; onDone: () => void }) {
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const state = s.removed
    ? `Removed${s.removedReason ? `: ${s.removedReason}` : ""}`
    : s.hidden
      ? "Hidden automatically (3 reports) — not playing"
      : "Live — still in the lineup";
  return (
    <li className={`panel stack report-item${s.open ? " open" : ""}`} data-testid="report-item">
      <div className="row">
        <strong>{s.title ?? "Untitled"}</strong>
        <span className="muted">by {s.author ?? "former contributor"}</span>
        <span className="chip">{s.bpm === 72 ? "The Infinite 72" : "The Infinite"}</span>
        {s.open > 0 ? (
          <span className="chip report-count">
            {s.open} open report{s.open === 1 ? "" : "s"}
          </span>
        ) : (
          <span className="chip">{s.outcome === "removed" ? "Removed" : "Kept"}</span>
        )}
      </div>
      <p className="small muted">
        {state}
        {s.playsAt ? ` · plays ${new Date(s.playsAt).toLocaleString()}` : ""}
      </p>
      <ul className="small report-reasons">
        {s.reports.map((r, i) => (
          <li key={i} className={r.handled ? "muted" : undefined}>
            <strong>{REASON_LABEL[r.reason] ?? r.reason}</strong>
            {r.note ? ` — “${r.note}”` : ""}{" "}
            <span className="muted">
              ({r.reporter ?? "deleted account"}, {new Date(r.createdAt).toLocaleString()})
            </span>
          </li>
        ))}
      </ul>
      <div className="row">
        <Link className="btn" href={`/loop/${s.loopId}`}>
          Open &amp; listen
        </Link>
        {!s.removed && s.open > 0 && (
          <button
            type="button"
            className="btn"
            disabled={busy}
            data-testid="report-keep"
            onClick={async () => {
              setBusy(true);
              setMsg("");
              try {
                const r = await api<{ restored: boolean }>(
                  "POST",
                  `/api/admin/reports/${encodeURIComponent(s.loopId)}/keep`,
                );
                setMsg(r.restored ? "Kept — it's back in the lineup." : "Kept.");
                onDone();
              } catch (e) {
                setMsg(e instanceof ApiError ? e.message : String(e));
              } finally {
                setBusy(false);
              }
            }}
          >
            Keep
          </button>
        )}
      </div>
      {!s.removed && <RemoveSongButton loopId={s.loopId} onRemoved={onDone} />}
      {msg && <p role="status">{msg}</p>}
    </li>
  );
}

function Reports({ onCount }: { onCount: (n: number) => void }) {
  const [data, error, reload] = useLoad<{ open: number; songs: ReportedSong[] }>("/api/admin/reports");
  useEffect(() => {
    if (data) onCount(data.open);
  }, [data, onCount]);
  useEffect(() => {
    const id = setInterval(reload, 15000);
    return () => clearInterval(id);
  }, [reload]);
  const open = data?.songs.filter((s) => s.open > 0) ?? [];
  const done = data?.songs.filter((s) => s.open === 0) ?? [];
  return (
    <>
      <Section title={`Reported songs (${open.length} open)`}>
        <p className="muted small">
          Listeners report songs from the song card in the River and from song pages. Three reports from accounts at
          least 7 days old hide a song at once (it's then re-checked automatically). Keep closes the reports and brings
          a hidden song back; Remove now takes it out for everyone, even while it plays, and emails the author the
          reason.
        </p>
        {error && <p role="alert">{error}</p>}
        {data && open.length === 0 && <p>No open reports.</p>}
        <ul className="stack plain-list">
          {open.map((s) => (
            <ReportedSongItem key={s.loopId} s={s} onDone={reload} />
          ))}
        </ul>
      </Section>
      {done.length > 0 && (
        <Section title="Handled">
          <ul className="stack plain-list">
            {done.map((s) => (
              <ReportedSongItem key={s.loopId} s={s} onDone={reload} />
            ))}
          </ul>
        </Section>
      )}
    </>
  );
}

interface PendingSong {
  loopId: string;
  bpm: number;
  day: number;
  title: string | null;
  author: string | null;
  wizardShare: number;
  submittedAt: number;
  freezesAt: number;
  missed: boolean;
}

function ReviewItem({ p, onDone }: { p: PendingSong; onDone: () => void }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  async function decide(action: "approve" | "reject") {
    setBusy(true);
    setMsg("");
    try {
      await api(
        "POST",
        `/api/admin/review/${encodeURIComponent(p.loopId)}/${action}`,
        action === "reject" ? { reason } : {},
      );
      onDone();
    } catch (e) {
      setMsg(e instanceof ApiError ? e.message : String(e));
      setBusy(false);
    }
  }
  const frozen = p.freezesAt <= Date.now();
  return (
    <li className="panel stack review-item" data-testid="review-item">
      <div className="row">
        <strong>{p.title ?? "Untitled"}</strong>
        <span className="muted">by {p.author ?? "deleted account"}</span>
        <span className="chip">{p.bpm === 72 ? "The Infinite 72" : "The Infinite"}</span>
        {p.wizardShare > 0 && <span className="chip">{Math.round(p.wizardShare * 100)} % Wizard</span>}
      </div>
      <p className="small muted">
        Submitted {new Date(p.submittedAt).toLocaleString()} ·{" "}
        {p.missed
          ? "its day is over — it can only be rejected"
          : frozen
            ? "its day is frozen — approving puts it in the next free Wizard slot"
            : `day freezes ${new Date(p.freezesAt).toLocaleString()}`}
      </p>
      <div className="row">
        <Link className="btn" href={`/loop/${p.loopId}`}>
          Open &amp; listen
        </Link>
        <button
          type="button"
          className="btn btn-primary"
          disabled={busy || p.missed}
          onClick={() => void decide("approve")}
          data-testid="review-approve"
        >
          Approve
        </button>
        <input
          type="text"
          value={reason}
          maxLength={500}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Reason for the author (when rejecting)"
          aria-label="Reason for rejecting"
          data-testid="review-reason"
        />
        <button
          type="button"
          className="btn danger"
          disabled={busy}
          onClick={() => void decide("reject")}
          data-testid="review-reject"
        >
          Reject
        </button>
      </div>
      {msg && <p role="alert">{msg}</p>}
    </li>
  );
}

function Review() {
  const [data, error, reload] = useLoad<{ pending: PendingSong[] }>("/api/admin/review");
  useEffect(() => {
    const id = setInterval(reload, 15000);
    return () => clearInterval(id);
  }, [reload]);
  return (
    <Section title={`Review queue (${data?.pending.length ?? 0})`}>
      <p className="muted small">
        A contributor's first songs that pass the automatic checks wait here before they join a lineup (Thresholds &amp;
        limits → review: on/off and how many songs). Rejecting sends the reason to the author, who can fix the song and
        submit it again.
      </p>
      {error && <p role="alert">{error}</p>}
      {data && data.pending.length === 0 && <p>Nothing waiting.</p>}
      <ul className="stack plain-list">
        {data?.pending.map((p) => (
          <ReviewItem key={p.loopId} p={p} onDone={reload} />
        ))}
      </ul>
    </Section>
  );
}

interface LiveEntry {
  slot: number;
  day: number;
  loopId: string;
  author: string | null;
  title: string | null;
  kind: "human" | "filler";
  repeat?: number;
  startMs: number;
  now: boolean;
}

function Live() {
  const [data, error, reload] = useLoad<{ now: number; streams: Record<string, LiveEntry[]> }>("/api/admin/now");
  useEffect(() => {
    const id = setInterval(reload, 5000);
    return () => clearInterval(id);
  }, [reload]);
  return (
    <>
      {error && <p role="alert">{error}</p>}
      {(["125", "72"] as const).map((bpm) => (
        <Section key={bpm} title={bpm === "72" ? "The Infinite 72 — now and next" : "The Infinite — now and next"}>
          <table>
            <thead>
              <tr>
                <th scope="col">Time</th>
                <th scope="col">Song</th>
                <th scope="col">
                  <span className="sr-only">Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {data?.streams[bpm]?.map((e) => (
                <tr
                  key={`${e.day}:${e.slot}`}
                  className={e.now ? "now-row" : undefined}
                  data-testid={e.now ? "live-now" : undefined}
                >
                  <td>
                    {e.now ? <span className="chip now-chip">Now</span> : new Date(e.startMs).toLocaleTimeString()}
                  </td>
                  <td>
                    {e.kind === "filler" ? (
                      <span className="muted">Wizard example song</span>
                    ) : (
                      <>
                        <Link href={`/loop/${e.loopId}`}>{e.title ?? "Untitled"}</Link>{" "}
                        <span className="muted">by {e.author ?? "former contributor"}</span>
                        {e.repeat ? <span className="chip">encore {e.repeat + 1}</span> : null}
                      </>
                    )}
                  </td>
                  <td>{e.kind === "human" && <RemoveSongButton loopId={e.loopId} onRemoved={reload} />}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      ))}
    </>
  );
}

function Audit() {
  const [type, setType] = useState("");
  const [data, error] = useLoad<{
    events: { id: number; at: string; type: string; actorId: string | null; data: unknown }[];
  }>(`/api/admin/audit${type ? `?type=${encodeURIComponent(type)}` : ""}`);
  return (
    <Section title="Audit log">
      <label className="field">
        <span>Type</span>
        <input type="text" value={type} onChange={(e) => setType(e.target.value)} placeholder="e.g. loop.hidden" />
      </label>
      {error && <p role="alert">{error}</p>}
      <table>
        <thead>
          <tr>
            <th scope="col">When</th>
            <th scope="col">Event</th>
            <th scope="col">Details</th>
          </tr>
        </thead>
        <tbody>
          {data?.events.map((e) => (
            <tr key={e.id}>
              <td>{new Date(e.at).toLocaleString()}</td>
              <td>{e.type}</td>
              <td className="mono small">{JSON.stringify(e.data)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </Section>
  );
}

export function Admin() {
  useTitle("Admin");
  const [tab, setTab] = useState<Tab>("health");
  // Open reports and songs waiting for review, shown on their tabs from any tab.
  const [openReports, setOpenReports] = useState(0);
  const [pendingReviews, setPendingReviews] = useState(0);
  const isAdmin = useSession((s) => s.user?.isAdmin === true);
  useEffect(() => {
    if (!isAdmin) return;
    const load = () => {
      void api<{ open: number }>("GET", "/api/admin/reports")
        .then((r) => setOpenReports(r.open))
        .catch(() => undefined);
      void api<{ pending: unknown[] }>("GET", "/api/admin/review")
        .then((r) => setPendingReviews(r.pending.length))
        .catch(() => undefined);
    };
    load();
    const id = setInterval(load, 60_000);
    return () => clearInterval(id);
  }, [isAdmin]);
  const badge = (id: Tab) => (id === "reports" ? openReports : id === "review" ? pendingReviews : 0);
  return (
    <AdminShell>
      <h1>Overview &amp; moderation</h1>
      <p className="muted">
        Plan days in the <Link href="/admin/planner">Planner</Link>; test kits and instruments in the{" "}
        <Link href="/admin/lab">Sound Lab</Link>.
      </p>
      <div className="segmented admin-tabs" role="tablist" aria-label="Admin sections">
        {TABS.map(([id, label]) => (
          <button
            key={id}
            type="button"
            role="tab"
            aria-selected={tab === id}
            className={tab === id ? "on" : ""}
            onClick={() => setTab(id)}
            data-testid={`admin-tab-${id}`}
          >
            {label}
            {badge(id) > 0 && <span className="tab-badge"> ({badge(id)})</span>}
          </button>
        ))}
      </div>
      <div className="stack admin-body">
        {tab === "health" && <Health />}
        {tab === "review" && <Review />}
        {tab === "live" && <Live />}
        {tab === "config" && <Config />}
        {tab === "references" && <References />}
        {tab === "reports" && <Reports onCount={setOpenReports} />}
        {tab === "audit" && <Audit />}
      </div>
    </AdminShell>
  );
}
